import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Bed, Bath, Square, MapPin } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SaveButton } from "@/components/properties/SaveButton";
import { PropertyDetailDialog, Property } from "@/components/properties/PropertyDetailDialog";
import { propertyTypeLabels, placeholderProperties } from "@/lib/property-constants";

export const FeaturedProperties = React.forwardRef<HTMLElement>((_, ref) => {
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  
  const { data: properties } = useQuery({
    queryKey: ["featured-properties"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .eq("is_featured", true)
        .eq("is_available", true)
        .limit(3);

      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const displayProperties = properties?.length ? properties : placeholderProperties;

  return (
    <section ref={ref} className="py-20 bg-muted/50">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            Featured Properties
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Explore our handpicked selection of exceptional properties available for your next stay or investment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayProperties.map((property, index) => (
            <Card 
              key={property.id} 
              className="overflow-hidden group hover:shadow-warm transition-all duration-300 animate-slide-up cursor-pointer"
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => setSelectedProperty(property)}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={property.images?.[0] || "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=480&q=60"}
                  alt={property.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                  width={400}
                  height={300}
                />
                <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                  {propertyTypeLabels[property.property_type as keyof typeof propertyTypeLabels]}
                </Badge>
                <SaveButton propertyId={property.id} className="absolute top-4 right-4" />
              </div>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <MapPin className="h-4 w-4" />
                  <span>{property.city}, {property.state}</span>
                </div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                  {property.title}
                </h3>
                <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                  {property.description}
                </p>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  {property.bedrooms && (
                    <div className="flex items-center gap-1">
                      <Bed className="h-4 w-4" />
                      <span>{property.bedrooms} beds</span>
                    </div>
                  )}
                  {property.bathrooms && (
                    <div className="flex items-center gap-1">
                      <Bath className="h-4 w-4" />
                      <span>{property.bathrooms} baths</span>
                    </div>
                  )}
                  {property.square_feet && (
                    <div className="flex items-center gap-1">
                      <Square className="h-4 w-4" />
                      <span>{property.square_feet.toLocaleString()} sqft</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className="font-display text-xl font-bold text-primary">
                    ${property.price?.toLocaleString()}
                    <span className="text-sm font-normal text-muted-foreground">/{property.price_period}</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedProperty(property); }}>
                    View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button size="lg" asChild>
            <Link to="/properties">
              View All Properties
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>

          <PropertyDetailDialog
            property={selectedProperty}
            open={!!selectedProperty}
            onOpenChange={(open) => !open && setSelectedProperty(null)}
          />
        </div>
      </div>
    </section>
  );
});

FeaturedProperties.displayName = "FeaturedProperties";
